import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../services/authContext';
import api from '../../services/api';
import {
  joinChatThread,
  leaveChatThread,
  sendChatMessage,
  subscribeChatMessages,
} from '../../services/chatSocket';
import './MessageThread.css';

function senderLabel(message, isOwn, t) {
  if (isOwn) {
    return t('chat.you');
  }
  return message.senderId?.username
    || [message.senderId?.firstName, message.senderId?.lastName].filter(Boolean).join(' ')
    || (message.senderRole === 'psychologist' ? t('chat.psychologist') : t('chat.user'));
}

function isOwnMessage(message, currentUser) {
  if (!currentUser?.role || !message?.senderRole) {
    return false;
  }
  return currentUser.role === message.senderRole;
}

function appendMessage(messages, message) {
  if (!message?._id) {
    return messages;
  }
  if (messages.some((item) => String(item._id) === String(message._id))) {
    return messages;
  }
  return [...messages, message];
}

function MessageThread({ userId, psychologistId }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [connected, setConnected] = useState(false);
  const listRef = useRef(null);

  const thread = useMemo(() => {
    if (user?.role === 'psychologist') {
      return { clientUserId: userId, psychologistId: user._id };
    }
    return { clientUserId: user?._id, psychologistId };
  }, [user?._id, user?.role, userId, psychologistId]);

  const loadMessages = useCallback(async () => {
    if (!thread.clientUserId || !thread.psychologistId) {
      return;
    }

    try {
      setError('');
      const data = user?.role === 'psychologist'
        ? await api.getPsychologistUserMessages(thread.clientUserId)
        : await api.getMyMessages(thread.psychologistId);
      setMessages(data);
    } catch (err) {
      setError(err.message || 'Не удалось загрузить сообщения');
    } finally {
      setLoading(false);
    }
  }, [thread.clientUserId, thread.psychologistId, user?.role]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    if (!thread.clientUserId || !thread.psychologistId) {
      return undefined;
    }

    let active = true;

    const setup = async () => {
      try {
        await joinChatThread({
          userId: thread.clientUserId,
          psychologistId: thread.psychologistId,
        });
        if (active) {
          setConnected(true);
          setError('');
        }
      } catch (err) {
        if (active) {
          setConnected(false);
          setError(err.message || 'Не удалось подключиться к чату в реальном времени');
        }
      }
    };

    setup();

    const unsubscribe = subscribeChatMessages((message) => {
      const sameThread = String(message.userId) === String(thread.clientUserId)
        && String(message.psychologistId) === String(thread.psychologistId);
      if (!sameThread) {
        return;
      }
      setMessages((current) => appendMessage(current, message));
    });

    return () => {
      active = false;
      unsubscribe();
      leaveChatThread({
        userId: thread.clientUserId,
        psychologistId: thread.psychologistId,
      });
      setConnected(false);
    };
  }, [thread.clientUserId, thread.psychologistId, user?._id, user?.role]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = useCallback(async () => {
    if (!text.trim() || sending || !thread.clientUserId || !thread.psychologistId) {
      return;
    }

    try {
      setSending(true);
      setError('');
      const message = await sendChatMessage({
        userId: thread.clientUserId,
        psychologistId: thread.psychologistId,
        text: text.trim(),
      });
      setText('');
      setMessages((current) => appendMessage(current, message));
    } catch (err) {
      setError(err.message || 'Не удалось отправить сообщение');
    } finally {
      setSending(false);
    }
  }, [sending, text, thread.clientUserId, thread.psychologistId]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    await sendMessage();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  if (loading) {
    return <p className="text-muted">{t('auth.loading')}</p>;
  }

  return (
    <div className="message-thread">
      {error && <div className="alert alert-danger py-2">{error}</div>}
      {connected && (
        <div className="message-thread__status text-muted small mb-2">
          {t('chat.realtime')}
        </div>
      )}
      <div className="message-thread__list" ref={listRef}>
        {messages.length === 0 ? (
          <p className="text-muted mb-0">{t('chat.empty')}</p>
        ) : (
          messages.map((message) => {
            const isOwn = isOwnMessage(message, user);
            return (
              <div
                key={message._id}
                className={`message-thread__item ${isOwn ? 'is-own' : 'is-other'}`}
              >
                <div className="message-thread__meta">
                  {senderLabel(message, isOwn, t)}
                  {' · '}
                  {new Date(message.createdAt).toLocaleString()}
                </div>
                <div className="message-thread__text">{message.text}</div>
              </div>
            );
          })
        )}
      </div>
      <form className="message-thread__form" onSubmit={handleSubmit}>
        <textarea
          className="form-control"
          rows={2}
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('chat.placeholder')}
          maxLength={5000}
        />
        <div className="message-thread__actions">
          <span className="message-thread__hint text-muted small">{t('chat.enterHint')}</span>
          <button type="submit" className="ui-btn ui-btn-primary" disabled={!text.trim() || sending}>
            {t('chat.send')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default MessageThread;
